import React, { useState } from 'react';
import { Button, CardActions, useTheme, Box, Typography, Modal, IconButton, List, ListItem, ListItemIcon, ListItemText, Snackbar, SnackbarContent, Avatar, AvatarGroup } from '@mui/material';
import { ShareOutlined, FavoriteOutlined, FavoriteBorderOutlined, ChatBubbleOutlineOutlined, Close as CloseIcon, Link as LinkIcon,  Facebook as FacebookIcon, Twitter as TwitterIcon, LinkedIn as LinkedInIcon, WhatsApp as WhatsAppIcon, Check as CheckIcon } from '@mui/icons-material';
import { Feed } from '@/constants/types';
import { useAppDispatch, toggleFeedLike } from '@/store';
import { useAuth } from '@/hooks';
import { FacebookShareButton, TwitterShareButton, LinkedinShareButton, WhatsappShareButton } from 'react-share';
import DOMPurify from 'dompurify';

interface FeedActionsProps {
  feed: Feed;
  onCommentClick?: () => void;
}

export const FeedActions: React.FC<FeedActionsProps> = ({ feed, onCommentClick }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleLikeToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(toggleFeedLike({
      targetType: 'feed',
      feedId: feed._id,
      targetId: feed._id
    }));
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShareModalOpen(true);
  };

  const handleShareClose = () => {
    setShareModalOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleCommentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCommentClick?.();
  };

  const stripHtml = (html: string) => {
    const clean = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
    return clean.substring(0, 200);
  };

  const getShareUrl = () => {
    // if (feed.feedType?.name?.toLowerCase() === 'job') {
    //   return `https://hrtalky.in/jobs/seo?q=${encodeURIComponent(feed.title || '')}`;
    // }
  //  if (feed.feedType?.name?.toLowerCase() === 'post') {
      return `https://hrtalky.in/feed/${(feed._id || '')}`;
    // }
    // return `${window.location.origin}/talky?feedId=${feed._id}`;
  };

  const handleCopyLink = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const url = getShareUrl();
    navigator.clipboard.writeText(url).then(() => {
      setSnackbarOpen(true);
      setShareModalOpen(false);
    }).catch(() => {
      console.log("Failed to copy");
    });
  };

  const shareUrl = getShareUrl();
  const shareTitle = feed.title || 'Check this out on HRTalky';
  const shareDescription = stripHtml(feed.content || '');

  // Create WhatsApp message with logged user info
  const userName = user?.name || 'HRTalky User';
  const userNumber = user?.whatsappNumber || '';
  const whatsappMessage = `Hi 👋\n\n*${userName}* shared this with you:\n\n*${shareTitle}*\n\n${shareDescription}\n\n${shareUrl}\n\n${userNumber ? `Contact: ${userNumber}\n` : ''}#HRTalky #Jobs #HR`;

  interface SocialIcon {
    icon: React.ReactNode;
    label: string;
    onClick?: () => void;
    component?: any;
    props?: any;
  }

  const socialIcons: SocialIcon[] = [
    { icon: <LinkIcon />, label: "Copy link", onClick: handleCopyLink },
    // {
    //   component: EmailShareButton,
    //   icon: <EmailIcon />,
    //   label: "Email",
    //   props: {
    //     subject: shareTitle,
    //     body: `${shareDescription}\n\nCheck this out: ${shareUrl}`
    //   }
    // },
    {
      component: FacebookShareButton,
      icon: <FacebookIcon />,
      label: "Facebook",
      props: { hashtag: "#HRTalky" }
    },
    {
      component: TwitterShareButton,
      icon: <TwitterIcon />,
      label: "X",
      props: { title: `${shareTitle}\n\n${shareDescription}\n\n`, hashtags: ['HRTalky', 'Jobs', 'HR'] }
    },
    {
      component: LinkedinShareButton,
      icon: <LinkedInIcon />,
      label: "LinkedIn",
      props: { title: shareTitle, summary: shareDescription, source: "HRTalky" }
    },
    {
      component: WhatsappShareButton,
      icon: <WhatsAppIcon />,
      label: "WhatsApp",
      props: { title: whatsappMessage, separator: "" }
    },
  ];
  return (
    <Box onClick={(e) => e.stopPropagation()}>
      {/* Stats Row - LinkedIn Style */}
      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {(feed.stats?.likes || feed.likesCount || 0) > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#e60023', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FavoriteOutlined sx={{ fontSize: 10, color: 'white' }} />
                </Box>
              </Box>
              {/* Show liked users */}
              {feed.likedUsers && feed.likedUsers.length > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                  <AvatarGroup
                    max={2}
                    sx={{
                      '& .MuiAvatar-root': {
                        width: 18,
                        height: 18,
                        fontSize: '0.6rem',
                        border: '1px solid white',
                      },
                    }}
                  >
                    {feed.likedUsers.slice(0, 2).map((likedUser) => (
                      <Avatar
                        key={likedUser._id}
                        src={likedUser.profilePicture?.url}
                        sx={{ bgcolor: 'primary.main' }}
                      >
                        {likedUser.name?.charAt(0)?.toUpperCase()}
                      </Avatar>
                    ))}
                  </AvatarGroup>
                  <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                    {feed.likedUsers.slice(0, 2).map(u => u.name?.split(' ')[0]).join(', ')}
                    {(feed.stats?.likes || feed.likesCount || 0) > 2 && (
                      <> and {(feed.stats?.likes || feed.likesCount || 0) - 2} others</>
                    )}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                  {feed.stats?.likes || feed.likesCount || 0}
                </Typography>
              )}
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {(feed.stats?.comments || 0) > 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: '#0a66c2' } }} onClick={handleCommentClick}>
              {feed.stats?.comments || 0} comments
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {feed.stats?.views || 0} views
          </Typography>
        </Box>
      </Box>

      {/* Actions Section - LinkedIn Style */}
      <CardActions
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          px: 1,
          py: 0.5,
          justifyContent: 'space-around',
        }}
      >
        <Button
          startIcon={feed.isMeLiked || feed.isLiked ? <FavoriteOutlined sx={{ fontSize: 20 }} /> : <FavoriteBorderOutlined sx={{ fontSize: 20 }} />}
          onClick={handleLikeToggle}
          sx={{
            flex: 1,
            textTransform: 'none',
            py: 1,
            color: feed.isMeLiked || feed.isLiked ? '#e60023' : 'text.secondary',
            fontWeight: 600,
            fontSize: '0.8rem',
            borderRadius: 1,
            '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#f3f2ef' : 'action.hover' },
          }}
        >
          Like
        </Button>

        <Button
          startIcon={<ChatBubbleOutlineOutlined sx={{ fontSize: 20 }} />}
          onClick={handleCommentClick}
          sx={{
            flex: 1,
            textTransform: 'none',
            py: 1,
            color: 'text.secondary',
            fontWeight: 600,
            fontSize: '0.8rem',
            borderRadius: 1,
            '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#f3f2ef' : 'action.hover' },
          }}
        >
          Comment
        </Button>

        <Button
          startIcon={<ShareOutlined sx={{ fontSize: 20 }} />}
          onClick={handleShareClick}
          sx={{
            flex: 1,
            textTransform: 'none',
            py: 1,
            color: 'text.secondary',
            fontWeight: 600,
            fontSize: '0.8rem',
            borderRadius: 1,
            '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#f3f2ef' : 'action.hover' },
          }}
        >
          Share
        </Button>
      </CardActions>

      {/* Share Modal */}
      <Modal open={shareModalOpen} onClose={handleShareClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 350,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: '12px',
            '& .react-share__ShareButton': {
              // width:'0 !important'
            },
            '& .MuiListItemText-root': {
              width: '100% !important'
            },
            '& .MuiListItemText-root .MuiTypography-root': {
              width: '100% !important'
            }
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
            Share Post
          </Typography>
          <List>
            {socialIcons.map((social, index) => {
              if (social.onClick) {
                return (
                  <ListItem
                    key={index}
                    onClick={social.onClick}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, mr: 1 }}>{social.icon}</ListItemIcon>
                    <ListItemText primary={social.label} />
                  </ListItem>
                );
              } else if (social.component) {
                const ShareButtonComponent = social.component;
                return (
                  <ListItem
                    key={index}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      p: 0
                    }}
                  >
                    <ShareButtonComponent
                      url={shareUrl}
                      {...(social.props || {})}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 16px',
                        textDecoration: 'none',
                        color: 'inherit'
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36, mr: 1 }}>{social.icon}</ListItemIcon>
                      <ListItemText primary={social.label} />
                    </ShareButtonComponent>
                  </ListItem>
                );
              } else {
                return null;
              }
            })}
          </List>
        </Box>
      </Modal>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <SnackbarContent
          sx={{
            bgcolor: 'success.main',
            color: 'white',
            borderRadius: 2,
            boxShadow: 3,
          }}
          message={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon />
              <Typography variant="body2" sx={{color:'white'}}>Link copied to clipboard!</Typography>
            </Box>
          }
          action={
            <IconButton onClick={handleSnackbarClose} color="inherit" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      </Snackbar>
    </Box>
  );
};